// @context TGW-1769 Slack風チャンネルUIの表示用純関数群。コンポーネントから切り離して
//   Jest で単体テスト可能にするため純関数で実装。本文HTMLはBEの body_url_to_link
//   （エスケープ+リンク化済み）を正とし、FEで再エスケープ/再リンク化はしない（二重処理防止）。
//   ここでは「表示の組み立て」（グルーピング・日付区切り・@装飾・自分宛判定）だけを担う。

const GROUP_WINDOW_MS = 5 * 60 * 1000;

// @channel/@everyone は集合宛、それ以外は @名前（composer が空白なしで挿入する前提）。
// メールアドレス誤爆を防ぐため、@ の直前が行頭・空白・タグ境界(>)のときだけ装飾する。
const MENTION_TOKEN_RE = /(^|[\s>]|&nbsp;)(@(?:channel|everyone|[^\s<&@]+))/g;

function sameDay(a, b) {
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

// 直前メッセージと「同一送信者・5分以内・同日」なら Slack 風にアバター/名前を省略する
export function isGroupedWithPrev(message, prev) {
  if (!prev || !message) return false;
  if (!prev.user || !message.user || prev.user.id !== message.user.id)
    return false;
  if (!sameDay(prev.created_at, message.created_at)) return false;

  const diff =
    new Date(message.created_at).getTime() -
    new Date(prev.created_at).getTime();
  return diff >= 0 && diff <= GROUP_WINDOW_MS;
}

export function needsDateSeparator(message, prev) {
  if (!prev) return true;
  return !sameDay(prev.created_at, message.created_at);
}

const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

export function dateSeparatorLabel(createdAt, now = new Date()) {
  const d = new Date(createdAt);
  const base = `${d.getMonth() + 1}月${d.getDate()}日(${WEEKDAYS[d.getDay()]})`;
  if (d.getFullYear() !== now.getFullYear()) {
    return `${d.getFullYear()}年${base}`;
  }
  if (sameDay(d, now)) return '今日';
  return base;
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// body_url_to_link 済みHTMLのテキスト部分だけを対象に @トークンを装飾する。
// タグ内部（属性値）を触らないよう、タグ区切りで分割してテキスト断片のみ置換する。
// @context TGW-1853 本文には安定キー @tunagate_id が入っている。mentionMap（{tunagate_id: {name, member_id}}）
//   があれば、その閲覧者文脈の表示名（@name）に差し替える。member_id があればプロフィールへのリンクにする。
//   差し替え名はBE素のテキストのため XSS防止に必ずエスケープする（body由来トークンはBEでエスケープ済み）。
export function decorateMentions(html, mentionMap = {}) {
  if (!html) return html;
  return String(html)
    .split(/(<[^>]*>)/)
    .map((part) => {
      if (part.startsWith('<')) return part; // タグはそのまま
      return part.replace(MENTION_TOKEN_RE, (_whole, lead, token) => {
        const handle = token.slice(1);
        const info =
          handle !== 'channel' && handle !== 'everyone'
            ? mentionMap[handle]
            : null;
        if (info && info.name) {
          const label = `@${escapeHtml(info.name)}`;
          if (info.member_id) {
            const href = `/member/profile/${escapeHtml(String(info.member_id))}`;
            return `${lead}<a href="${href}" class="slackch-mention">${label}</a>`;
          }
          return `${lead}<span class="slackch-mention">${label}</span>`;
        }
        return `${lead}<span class="slackch-mention">${token}</span>`;
      });
    })
    .join('');
}

// message.mentions（[{tunagate_id, name, member_id, ...}]）から
// {handle: {name, member_id}} の逆引きmapを作る。
// @context TGW-2018 本文トークンは新形式 @member_id と旧形式 @tunagate_id（既存メッセージ・
//   旧クライアント発）が混在するため、両方のキーで引けるようにする。キー衝突時は
//   不変キーである member_id を優先（後勝ちで member_id を後に登録）。
export function mentionNameMap(message) {
  const map = {};
  ((message && message.mentions) || []).forEach((m) => {
    if (!m || !m.name) return;
    const info = { name: m.name, member_id: m.member_id };
    if (m.tunagate_id) map[m.tunagate_id] = info;
  });
  ((message && message.mentions) || []).forEach((m) => {
    if (!m || !m.name) return;
    if (m.member_id) {
      map[String(m.member_id)] = { name: m.name, member_id: m.member_id };
    }
  });
  return map;
}

// 自分宛か（個人メンション or @channel/@everyone）。BEの Message#mentions を正とする。
const MENTION_TYPE_CHANNEL = 2;
const MENTION_TYPE_EVERYONE = 3;

export function isMentionedMe(message, currentUserId) {
  const mentions = (message && message.mentions) || [];
  return mentions.some(
    (m) =>
      m.mentioned_user_id === currentUserId ||
      m.mention_type === MENTION_TYPE_CHANNEL ||
      m.mention_type === MENTION_TYPE_EVERYONE,
  );
}

export default {
  isGroupedWithPrev,
  needsDateSeparator,
  dateSeparatorLabel,
  decorateMentions,
  mentionNameMap,
  isMentionedMe,
};
